// University of Wyoming: public college page (#2840), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UW Common Data Set 2025-2026 (PDF from the Office of Institutional
//    Analysis, cds2025-2026.pdf): C1 applicants, admits and enrollees for
//    Fall 2025 with the residency table (one applicant of unknown residency,
//    admitted, is left out of the rows), C8 test policy for Fall 2027,
//    C16 (rolling decisions), C21/C22 (no early decision, no early action),
//    G1/G5 2026-2027 costs (not marked as estimates).
//  - UW Admissions: First-Year Undergraduate Admission (rolling admission,
//    $40 fee, Common App, GPA and High School Success Curriculum), Apply to
//    the University of Wyoming (requirements and FAQs: no single deadline,
//    confirm by May 1 for financial aid, scores not part of the admission
//    requirements) and Admission With Support (2.50 to 2.99 GPA, Bridge
//    Program by July 15). None of these pages names an entering year; they
//    describe a standing rolling policy, read here as this cycle's.
//  - UW 2026-2027 Catalog (majors file).
//  - College Scorecard, school id 240727 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Quirks: the CDS C14 still lists an August 10 fall closing date, but the
// admissions pages say UW has no single application deadline; the page
// follows the admissions pages. Testing: the admissions pages say scores
// are not required and can raise financial awards, and the CDS says UW
// does not use them in admission decisions (C8A "No", C7 "Not Considered",
// C8 "Not considered for admission, even if submitted" for Fall 2027), so
// the policy is shown as test-blind for admission.
// Cost: CDS G1 first-year figures (fees include about $510 in program fees
// and the $150 first-year matriculation fee, per G1) with G5 resident
// books, transportation and other expenses.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UW Common Data Set 2025-2026',
        url: 'https://www.uwyo.edu/oia/_files/cds/cds2025-2026.pdf',
        read: '2026-10-01',
    },
    freshman: {
        label: 'UW Admissions, First-Year Undergraduate Admission',
        url: 'https://www.uwyo.edu/admissions/freshman/index.html',
        read: '2026-10-01',
    },
    apply: {
        label: 'UW Admissions, Apply to the University of Wyoming',
        url: 'https://www.uwyo.edu/admissions/apply.html',
        read: '2026-10-01',
    },
    support: {
        label: 'UW Admissions, Admission With Support',
        url: 'https://www.uwyo.edu/admissions/freshman/admission-with-support.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?240727-University-of-Wyoming',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-wyoming',
    kbId: 'university_of_wyoming',
    name: 'University of Wyoming',
    shortName: 'Wyoming',
    aliases: ['UW', 'UWyo'],
    location: 'Laramie, WY',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UW branding and templates page); decoration only.
    branding: {
        primary: '#492F24',
        secondary: '#FFC425',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_wyoming_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 6167,
        admitted: 5979,
        enrolled: 1444,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state (Wyoming)', applied: 1710, admitted: 1662 },
            { group: 'Out-of-state', applied: 4360, admitted: 4229 },
            { group: 'International', applied: 96, admitted: 87 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UW\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'UW has no early decision or early action plan. It admits on a rolling basis, and its Common Data Set says decisions go out within 2 weeks of receiving the required materials.',
        source: 'cds',
        where: 'sections C16, C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to UW, not to a college. Admission rests on your high school GPA and the High School Success Curriculum (4 years each of English, math and science, 3 of social science and 4 of additional coursework):',
        rows: [
            { school: 'Assured admission', requirement: 'A cumulative, unweighted high school GPA of 3.00 to 4.00 assures admission.' },
            { school: 'Admission with support', requirement: 'A GPA of 2.50 to 2.99, or 2.25 to 2.49 after a holistic review that may ask for an essay, with no more than two curriculum deficiencies (not both in one category). Students admitted this way must register for the Bridge Program by July 15 before fall classes begin.', source: 'support' },
        ],
        source: 'apply',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application deadline', value: 'None (rolling admission)' },
        rows: [
            { plan: 'Rolling admission', deadline: 'No single deadline', decision: null, binding: false },
        ],
        note: 'UW reviews applications as they arrive, and you can apply any time before the semester starts; applying earlier leaves time for housing, onboarding and aid planning. To be considered for financial aid, confirm your enrollment by May 1. The application fee is $40 and is not refundable. UW does not publish decision dates.',
        source: 'apply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-blind',
        blind: true,
        line: 'UW does not require the SAT or ACT for admission. Scores can help you secure higher financial awards, so UW recommends sending them with your application; its Common Data Set says they are not considered in the admission decision itself.',
        source: 'freshman',
        cdsLine: 'The Common Data Set says UW does not use SAT or ACT scores in admission decisions and marks them not considered for admission, even if submitted, for fall 2027 applicants.',
        cdsWhere: 'sections C7 and C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [5610, 22470] },
            { item: 'Required fees', amounts: [3070, 3070] },
            { item: 'Housing (on campus)', amounts: [6450, 6450] },
            { item: 'Food (on-campus meal plan)', amounts: [7956, 7956] },
            { item: 'Books and supplies', amounts: [1300, 1300] },
            { item: 'Transportation', amounts: [1800, 1800] },
            { item: 'Other expenses', amounts: [2300, 2300] },
        ],
        totals: [28486, 45346],
        note: 'For a full-time first-year student living on campus. The required fees include typical program fees of about $510 a year and the $150 first-year matriculation fee; program fees vary by course.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 13647,
            byIncome: [
                { income: '$0 to $30,000', amount: 9037 },
                { income: '$30,001 to $48,000', amount: 8764 },
                { income: '$48,001 to $75,000', amount: 9728 },
                { income: '$75,001 to $110,000', amount: 13949 },
                { income: 'Over $110,000', amount: 20896 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UW\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
